import { Users, Bell, Calendar, TrendingUp, AlertCircle, CheckCircle2 } from "lucide-react";
import { PageHeader } from "../../components/shared/PageHeader";
import { Card, TONE_ACCENT } from "../../components/shared/Card";
import { Badge } from "../../components/shared/Badge";
import { StatusBadge } from "../../components/shared/StatusBadge";
import { ROLES } from "../../lib/rbac";
import { useAppData } from "../../lib/AppDataContext";
import { useAuth } from "../../lib/AuthContext";
import { isAssignedTo, getAssignees, isToday, allowsFollowUp, since } from "../../lib/leads";

/* Dashboard — 5 KPI tiles matching the latest OLD admin layout:
     Total Leads → Today's Leads → Today's Follow-up →
     (Converted | Unassigned, role-dependent) → Booked Leads

   The 4th card branches by role (sales sees Converted Leads = their own
   bookings; admin/super_admin see Unassigned Leads since routing is their
   concern). The 5th — Booked Leads — is always shown so the bookings
   pulse from the team is visible at a glance regardless of role.

   Below the KPIs: New leads card (status === "new", newest first, top 6)
   + Today's schedule card (follow-ups in active statuses, time order).
   Sales role sees only their own leads (modeled as s1) throughout. */
export function Dashboard({ role = "super_admin" }) {
  const { leadsData, allSalesPeople } = useAppData();
  const allLeads = leadsData || [];
  const { user } = useAuth();
  // Phase 5 Bundle A — filter by logged-in user.id (was hardcoded "s1" mock)
  const myLeads = role === "employee"
    ? allLeads.filter((l) => isAssignedTo(l, user?.id))
    : allLeads;

  const todaysLeads = myLeads.filter((l) => isToday(l.createdAt));
  const todaysFollowUps = myLeads.filter((l) => isToday(l.followUpAt) && allowsFollowUp(l.status));
  const unassigned = myLeads.filter((l) => getAssignees(l).length === 0);
  const converted = myLeads.filter((l) => l.status === "booked");
  const booked = myLeads.filter((l) => l.status === "booked");

  // Tones mirror LeadsList so the same metric is the same colour on both
  // screens. indigo = foundational count, blue = new, amber = due,
  // red = routing gap, green = closed.
  const kpis = [
    { label: "Total Leads",       value: myLeads.length,         sub: role === "employee" ? "assigned to you" : "in the pipeline", icon: Users, tone: "indigo" },
    { label: "Today's Leads",     value: todaysLeads.length,     sub: "captured today",                                         icon: Bell, tone: "info" },
    { label: "Today's Follow-up", value: todaysFollowUps.length, sub: "scheduled for today",                                    icon: Calendar, tone: "warning" },
    role === "employee"
      ? { label: "Converted Leads",  value: converted.length,    sub: converted.length > 0 ? "bookings closed" : "none yet",     icon: TrendingUp, tone: "success" }
      : { label: "Unassigned Leads", value: unassigned.length,   sub: unassigned.length > 0 ? "needs routing" : "all assigned",  icon: AlertCircle, tone: "danger" },
    { label: "Booked Leads",      value: booked.length,          sub: booked.length > 0 ? "deals closed" : "none yet",          icon: CheckCircle2, tone: "success" },
  ];

  const newLeads = myLeads
    .filter((l) => l.status === "new")
    .sort((a, b) => b.createdAt - a.createdAt)
    .slice(0, 6);

  const schedule = myLeads
    .filter((l) => isToday(l.followUpAt) && allowsFollowUp(l.status))
    .sort((a, b) => a.followUpAt - b.followUpAt);

  const teamById = Object.fromEntries(allSalesPeople.map((s) => [s.userId || s.id, s]));  // Phase 5: key by user.id
  const formatTime = (d) => d.toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit", hour12: true });

  return (
    <div>
      <PageHeader accent="sales" title="Dashboard" subtitle={`Welcome back · ${ROLES[role].label}`} />

      {/* 5-column KPI strip. Two columns on mobile, three at sm, five at
          lg. The split mirrors the OLD admin's responsive shape — wide
          screens get one row, narrow screens wrap cleanly. */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
        {kpis.map((k) => (
          <Card key={k.label} tone={k.tone} className="p-4">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-slate-600">{k.label}</span>
              <span className={`w-7 h-7 rounded-lg grid place-items-center shrink-0 ${(TONE_ACCENT[k.tone] || TONE_ACCENT.default).chip}`}>
                <k.icon className="w-4 h-4 text-white" />
              </span>
            </div>
            <div className={`text-2xl font-bold mt-2 tabular-nums ${(TONE_ACCENT[k.tone] || TONE_ACCENT.default).text}`}>{k.value}</div>
            <div className="text-xs text-slate-500 mt-1">{k.sub}</div>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* New leads — status === "new", newest first. */}
        <Card className="p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-slate-900">New leads</h3>
            <span className="text-xs text-slate-400">{newLeads.length} awaiting first contact</span>
          </div>
          {newLeads.length === 0 ? (
            <div className="py-8 text-center text-sm text-slate-400">No new leads &mdash; you're all caught up.</div>
          ) : (
            <div className="space-y-1">
              {newLeads.map((l) => (
                <div key={l.id} className="flex items-center gap-3 py-2 px-2 -mx-2 rounded-lg hover:bg-slate-50">
                  <div className="w-8 h-8 rounded-full bg-slate-100 grid place-items-center text-xs font-semibold text-slate-600 shrink-0">
                    {l.name.split(" ").map((p) => p[0]).join("")}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-slate-900 truncate">{l.name}</div>
                    <div className="text-xs text-slate-500 truncate">{l.project} &middot; {l.source}</div>
                  </div>
                  <StatusBadge status={l.status} />
                  <span className="text-xs text-slate-400 shrink-0 w-16 text-right">{since(l.createdAt)}</span>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Today's schedule — follow-ups planned for the calendar day. */}
        <Card className="p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-slate-900">Today's schedule</h3>
            <span className="text-xs text-slate-400">{schedule.length} follow-ups planned</span>
          </div>
          {schedule.length === 0 ? (
            <div className="py-8 text-center text-sm text-slate-400">No follow-ups scheduled for today.</div>
          ) : (
            <div className="space-y-1">
              {schedule.map((l) => {
                const assigneeIds = getAssignees(l);
                const firstOwner = assigneeIds.length > 0 ? teamById[assigneeIds[0]] : null;
                const extraCount = Math.max(0, assigneeIds.length - 1);
                return (
                  <div key={l.id} className="flex items-center gap-3 py-2 px-2 -mx-2 rounded-lg hover:bg-slate-50">
                    <div className="w-14 text-xs font-semibold text-blue-700 tabular-nums shrink-0">{formatTime(l.followUpAt)}</div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-slate-900 truncate">{l.name}</div>
                      <div className="text-xs text-slate-500 truncate">{l.project}</div>
                    </div>
                    <StatusBadge status={l.status} />
                    {firstOwner && role !== "sales" && (
                      <Badge cls="bg-slate-100 text-slate-600 ring-slate-400/20 hidden sm:inline-flex">
                        {firstOwner.name.split(" ")[0]}{extraCount > 0 ? ` +${extraCount}` : ""}
                      </Badge>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
